import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleAdaptiveNavbar from '../../components/ui/RoleAdaptiveNavbar';
import NotificationIndicator from '../../components/ui/NotificationIndicator';
import GalleryNavigationBreadcrumb from '../../components/ui/GalleryNavigationBreadcrumb';
import MetricsCard from './components/MetricsCard';
import ContributorTable from './components/ContributorTable';
import EngagementMetricsPanel from './components/EngagementMetricsPanel';
import DownloadTrendsChart from './components/DownloadTrendsChart';
import VisitorAnalytics from './components/VisitorAnalytics';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import { userService } from '../../services/userService';
import { visitorService } from '../../services/visitorService';
import { exportUtils } from '../../utils/exportUtils';


const OwnerDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('contributors');
  const [contributors, setContributors] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [engagementMetrics, setEngagementMetrics] = useState(null);
  const [downloadTrends, setDownloadTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/owner-dashboard', icon: 'LayoutDashboard' }
  ];

  useEffect(() => {
    loadContributors();
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalyticsData();
    }
  }, [activeTab]);

  const loadAnalyticsData = async () => {
    try {
      setAnalyticsLoading(true);
      const [visitorsData, metricsData, trendsData] = await Promise.all([
        visitorService?.getAllVisitors(),
        visitorService?.getVisitorEngagementMetrics(),
        visitorService?.getDownloadTrends(30)
      ]);

      // Transform visitors data
      const transformedVisitors = visitorsData?.map(v => ({
        id: v?.id,
        name: v?.visitor_name,
        email: v?.visitor_email,
        phone: v?.visitor_phone,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(v?.visitor_name)}&background=random`,
        avatarAlt: `Profile picture of ${v?.visitor_name}`,
        galleryAccessed: v?.galleries?.gallery_name || 'Unknown Gallery',
        downloadCount: 0, // TODO: Track per-visitor downloads
        lastVisit: new Date(v?.last_visit_at),
        totalVisits: v?.visit_count
      }));

      setVisitors(transformedVisitors);
      setEngagementMetrics(metricsData);
      setDownloadTrends(trendsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err?.message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadContributors = async () => {
    try {
      setLoading(true);
      const data = await userService?.getAllContributors();
      
      // Transform to match existing component structure
      const transformedData = data?.map(user => ({
        id: user?.id,
        name: user?.name,
        email: user?.email,
        role: user?.role,
        avatar: user?.avatar || 'https://img.rocket.new/generatedImages/rocket_gen_img_1b43e8b7f-1763295504724.png',
        avatarAlt: `Profile picture of ${user?.name}`,
        galleryCount: 0, // TODO: Add gallery count from galleries table
        lastActive: new Date(user.updatedAt),
        status: user?.isActive ? 'active' : 'inactive'
      }));
      
      setContributors(transformedData);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading contributors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await userService?.updateUserRole(userId, newRole);
      // Reload contributors to reflect changes
      await loadContributors();
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Failed to update role: ' + err?.message);
    }
  };

  const handleEditContributor = (contributorId) => {
    console.log('Edit contributor:', contributorId);
  };

  const handleViewGalleries = (contributorId) => {
    navigate(`/gallery-management?contributor=${contributorId}`);
  };

  const handleDeleteContributor = async (contributorId) => {
    if (window.confirm('Are you sure you want to delete this contributor?')) {
      try {
        await userService?.toggleUserStatus(contributorId, false);
        await loadContributors();
      } catch (err) {
        console.error('Error deleting contributor:', err);
        alert('Failed to delete contributor: ' + err?.message);
      }
    }
  };

  const mockMetrics = [
    {
      title: 'Total Contributors',
      value: contributors?.length?.toString(),
      subtitle: 'Active accounts',
      icon: 'Users',
      iconColor: 'var(--color-primary)',
      trend: 'up',
      trendValue: '+2 this month'
    },
    {
      title: 'Active Galleries',
      value: '48',
      subtitle: 'Published galleries',
      icon: 'Images',
      iconColor: 'var(--color-success)',
      trend: 'up',
      trendValue: '+8 this week'
    },
    {
      title: 'Total Visitors',
      value: '324',
      subtitle: 'Unique visitors',
      icon: 'Eye',
      iconColor: 'var(--color-accent)',
      trend: 'up',
      trendValue: '+45 this week'
    },
    {
      title: 'Downloads',
      value: '1,847',
      subtitle: 'Total downloads',
      icon: 'Download',
      iconColor: 'var(--color-warning)',
      trend: 'up',
      trendValue: '+156 today'
    }
  ];


  const mockGalleries = [
  {
    id: 'GAL001',
    title: 'Corporate Event 2026',
    contributor: 'Sarah Johnson',
    contributorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b43e8b7f-1763295504724.png",
    contributorAvatarAlt: 'Professional headshot of woman with shoulder-length brown hair wearing navy blazer against white background',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_10676ac89-1766793242075.png",
    thumbnailAlt: 'Professional corporate event photography showing business professionals networking in modern conference hall with ambient lighting',
    mediaCount: 156,
    views: 89,
    downloads: 34,
    createdAt: new Date('2026-01-05T10:00:00'),
    status: 'active'
  },
  {
    id: 'GAL002',
    title: 'Product Launch Campaign',
    contributor: 'Michael Chen',
    contributorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd15b436-1763300581767.png",
    contributorAvatarAlt: 'Professional headshot of Asian man with short black hair wearing gray suit and glasses',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1e6bac9e6-1765063611093.png",
    thumbnailAlt: 'High-end product photography featuring sleek electronic device on minimalist white background with dramatic studio lighting',
    mediaCount: 203,
    views: 145,
    downloads: 67,
    createdAt: new Date('2026-01-08T14:30:00'),
    status: 'active'
  },
  {
    id: 'GAL003',
    title: 'Fashion Editorial Spring',
    contributor: 'Emily Rodriguez',
    contributorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_162a57531-1763296100992.png",
    contributorAvatarAlt: 'Professional headshot of Hispanic woman with long dark hair wearing white blouse',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a84c0168-1767105011129.png",
    thumbnailAlt: 'Fashion editorial photography showing model in flowing spring dress against natural outdoor setting with soft sunlight',
    mediaCount: 98,
    views: 67,
    downloads: 23,
    createdAt: new Date('2026-01-10T09:15:00'),
    status: 'active'
  },
  {
    id: 'GAL004',
    title: 'Architecture Portfolio',
    contributor: 'David Thompson',
    contributorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13d928a6c-1763298625896.png",
    contributorAvatarAlt: 'Professional headshot of man with short blonde hair wearing black turtleneck',
    thumbnail: "https://images.unsplash.com/photo-1707964456631-fde819f99cec",
    thumbnailAlt: 'Modern architectural photography showcasing contemporary building with glass facade and geometric design against blue sky',
    mediaCount: 124,
    views: 112,
    downloads: 45,
    createdAt: new Date('2026-01-11T16:00:00'),
    status: 'active'
  },
  {
    id: 'GAL005',
    title: 'Wedding Collection',
    contributor: 'Lisa Anderson',
    contributorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_120f9d597-1763293939407.png",
    contributorAvatarAlt: 'Professional headshot of woman with red hair wearing green blazer',
    thumbnail: "https://images.unsplash.com/photo-1698112458801-c293cac9ab99",
    thumbnailAlt: 'Romantic wedding photography capturing bride and groom in elegant ceremony setting with soft natural lighting',
    mediaCount: 187,
    views: 234,
    downloads: 89,
    createdAt: new Date('2026-01-03T11:30:00'),
    status: 'active'
  },
  {
    id: 'GAL006',
    title: 'Food & Beverage Shoot',
    contributor: 'Michael Chen',
    contributorAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd15b436-1763300581767.png",
    contributorAvatarAlt: 'Professional headshot of Asian man with short black hair wearing gray suit and glasses',
    thumbnail: "https://images.unsplash.com/photo-1632938053270-19372c218576",
    thumbnailAlt: 'Professional food photography featuring gourmet dish with artistic plating on dark background with dramatic lighting',
    mediaCount: 76,
    views: 56,
    downloads: 19,
    createdAt: new Date('2026-01-12T13:45:00'),
    status: 'active'
  }];


  const mockVisitors = [
  {
    id: 'VIS001',
    name: 'Jennifer Martinez',
    email: 'jennifer.m@clientcompany.com',
    phone: '+1 (555) 123-4567',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce85da65-1763296193559.png",
    avatarAlt: 'Professional headshot of woman with curly brown hair wearing business attire',
    galleryAccessed: 'Corporate Event 2026',
    downloadCount: 12,
    lastVisit: new Date('2026-01-13T15:30:00'),
    totalVisits: 5
  },
  {
    id: 'VIS002',
    name: 'Robert Williams',
    email: 'r.williams@marketing.com',
    phone: '+1 (555) 234-5678',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1eaa82255-1763292165824.png",
    avatarAlt: 'Professional headshot of man with gray hair wearing navy suit',
    galleryAccessed: 'Product Launch Campaign',
    downloadCount: 28,
    lastVisit: new Date('2026-01-13T14:15:00'),
    totalVisits: 8
  },
  {
    id: 'VIS003',
    name: 'Amanda Foster',
    email: 'amanda.foster@fashion.com',
    phone: '+1 (555) 345-6789',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e64c7e4a-1763294930430.png",
    avatarAlt: 'Professional headshot of woman with blonde hair wearing elegant dress',
    galleryAccessed: 'Fashion Editorial Spring',
    downloadCount: 15,
    lastVisit: new Date('2026-01-13T10:45:00'),
    totalVisits: 3
  },
  {
    id: 'VIS004',
    name: 'James Cooper',
    email: 'james.cooper@architecture.com',
    phone: '+1 (555) 456-7890',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1db2b5751-1763292830762.png",
    avatarAlt: 'Professional headshot of man with dark hair wearing casual blazer',
    galleryAccessed: 'Architecture Portfolio',
    downloadCount: 34,
    lastVisit: new Date('2026-01-13T09:20:00'),
    totalVisits: 12
  },
  {
    id: 'VIS005',
    name: 'Rachel Green',
    email: 'rachel.green@events.com',
    phone: '+1 (555) 567-8901',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1da6df2fa-1763297597959.png",
    avatarAlt: 'Professional headshot of woman with short dark hair wearing professional attire',
    galleryAccessed: 'Wedding Collection',
    downloadCount: 45,
    lastVisit: new Date('2026-01-12T16:30:00'),
    totalVisits: 15
  }];


  const mockActivities = [
  {
    id: 'ACT001',
    type: 'gallery_created',
    title: 'New Gallery Created',
    description: 'Food & Beverage Shoot gallery published',
    timestamp: new Date('2026-01-13T13:45:00'),
    user: {
      name: 'Michael Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd15b436-1763300581767.png",
      avatarAlt: 'Professional headshot of Asian man with short black hair wearing gray suit and glasses'
    }
  },
  {
    id: 'ACT002',
    type: 'visitor_accessed',
    title: 'Gallery Accessed',
    description: 'Jennifer Martinez viewed Corporate Event 2026',
    timestamp: new Date('2026-01-13T15:30:00'),
    user: {
      name: 'Jennifer Martinez',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce85da65-1763296193559.png",
      avatarAlt: 'Professional headshot of woman with curly brown hair wearing business attire'
    }
  },
  {
    id: 'ACT003',
    type: 'file_downloaded',
    title: 'Files Downloaded',
    description: '12 files downloaded from Product Launch Campaign',
    timestamp: new Date('2026-01-13T14:15:00'),
    user: {
      name: 'Robert Williams',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1eaa82255-1763292165824.png",
      avatarAlt: 'Professional headshot of man with gray hair wearing navy suit'
    }
  },
  {
    id: 'ACT004',
    type: 'contributor_added',
    title: 'New Contributor Added',
    description: 'Lisa Anderson joined as contributor',
    timestamp: new Date('2026-01-13T11:00:00'),
    user: null
  },
  {
    id: 'ACT005',
    type: 'gallery_created',
    title: 'New Gallery Created',
    description: 'Architecture Portfolio gallery published',
    timestamp: new Date('2026-01-11T16:00:00'),
    user: {
      name: 'David Thompson',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13d928a6c-1763298625896.png",
      avatarAlt: 'Professional headshot of man with short blonde hair wearing black turtleneck'
    }
  }];


  const mockNotifications = [
  {
    id: 'NOT001',
    type: 'visitor',
    title: 'New Visitor Registration',
    message: 'Jennifer Martinez registered for Corporate Event 2026',
    timestamp: new Date('2026-01-13T15:30:00'),
    read: false
  },
  {
    id: 'NOT002',
    type: 'gallery',
    title: 'Gallery Published',
    message: 'Michael Chen published Food & Beverage Shoot',
    timestamp: new Date('2026-01-13T13:45:00'),
    read: false
  },
  {
    id: 'NOT003',
    type: 'sync',
    title: 'Drive Sync Complete',
    message: 'Successfully synced 156 files from Google Drive',
    timestamp: new Date('2026-01-13T12:00:00'),
    read: true
  }];


  const handleContributorEdit = (id) => {
    console.log('Edit contributor:', id);
  };

  const handleContributorViewGalleries = (id) => {
    navigate('/gallery-management');
  };

  const handleContributorDelete = (id) => {
    console.log('Delete contributor:', id);
  };

  const handleGalleryEdit = (id) => {
    navigate('/gallery-creation');
  };

  const handleGalleryView = (id) => {
    navigate(`/gallery-viewer?id=${id}`);
  };

  const handleGalleryDelete = (id) => {
    console.log('Delete gallery:', id);
  };

  const handleExportData = async () => {
    try {
      const exportData = await visitorService?.exportVisitorData();
      const timestamp = new Date()?.toISOString()?.split('T')?.[0];
      exportUtils?.exportToCSV(exportData, `visitor-analytics-${timestamp}.csv`);
    } catch (err) {
      console.error('Error exporting data:', err);
      alert('Failed to export data: ' + err?.message);
    }
  };

  const handleExportFullReport = async () => {
    try {
      const exportData = await visitorService?.exportVisitorData();
      const reportData = exportUtils?.generateReportData(
        exportData,
        engagementMetrics,
        downloadTrends
      );
      const timestamp = new Date()?.toISOString()?.split('T')?.[0];
      exportUtils?.exportToJSON(reportData, `full-analytics-report-${timestamp}.json`);
    } catch (err) {
      console.error('Error exporting report:', err);
      alert('Failed to export report: ' + err?.message);
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'addContributor':console.log('Add contributor action');
        break;
      case 'createGallery':navigate('/gallery-creation');
        break;
      case 'driveSettings':console.log('Drive settings action');
        break;
      case 'exportData':
        handleExportData();
        break;
      default:
        break;
    }
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (id) => {
    console.log('Mark as read:', id);
  };

  const handleMarkAllAsRead = () => {
    console.log('Mark all as read');
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleAdaptiveNavbar userRole="owner" userName="Christian Owner" />
      <div className="pt-16">
        <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div>
              <GalleryNavigationBreadcrumb items={breadcrumbItems} />
              <h1 className="text-2xl md:text-3xl font-bold mt-2">Owner Dashboard</h1>
              <p className="text-muted-foreground mt-1">Manage contributors, galleries, and system settings</p>
            </div>
            
            <NotificationIndicator count={3} />

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {mockMetrics?.map((metric, index) =>
            <MetricsCard key={index} {...metric} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <QuickActions onAction={handleQuickAction} />
            </div>
            <div className="lg:col-span-1">
              <RecentActivity activities={mockActivities} />
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-4 md:p-6 mb-6">
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setActiveTab('contributors')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'contributors' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Contributors
              </button>
              <button
                onClick={() => setActiveTab('galleries')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'galleries' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Galleries
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'analytics' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Analytics
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-destructive">{error}</p>
              </div>
            ) : (
              <>
                {activeTab === 'contributors' && (
                  <ContributorTable
                    contributors={contributors}
                    onEdit={handleEditContributor}
                    onViewGalleries={handleViewGalleries}
                    onDelete={handleDeleteContributor}
                    onRoleChange={handleRoleChange}
                  />
                )}

                {activeTab === 'analytics' && (
                  <div className="space-y-6">
                    <EngagementMetricsPanel 
                      metrics={engagementMetrics} 
                      loading={analyticsLoading} 
                    />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <DownloadTrendsChart 
                        trends={downloadTrends} 
                        period={30} 
                      />
                      
                      <div className="bg-card rounded-xl border border-border p-4 md:p-6">
                        <h3 className="text-base md:text-lg font-semibold mb-4">Export Options</h3>
                        <div className="space-y-3">
                          <button
                            onClick={handleExportData}
                            className="w-full flex items-center justify-between p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                          >
                            <div className="text-left">
                              <p className="font-medium text-sm">Export Visitor Data (CSV)</p>
                              <p className="text-xs text-muted-foreground">Download visitor list with contact info</p>
                            </div>
                            <span className="text-primary">→</span>
                          </button>
                          
                          <button
                            onClick={handleExportFullReport}
                            className="w-full flex items-center justify-between p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                          >
                            <div className="text-left">
                              <p className="font-medium text-sm">Export Full Report (JSON)</p>
                              <p className="text-xs text-muted-foreground">Complete analytics with metrics and trends</p>
                            </div>
                            <span className="text-primary">→</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <VisitorAnalytics 
                      visitors={visitors} 
                      onExport={handleExportData} 
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>);

};

export default OwnerDashboard;